import React, { useEffect, useState, useCallback } from 'react';
import './ProyectosPendientes.css';
import { apiFetch } from '../../../services/dashboardApi';
import DashboardButton from './ui/DashboardButton';

const API_URL = 'http://localhost:8000/api';

function ProyectosPendientes({ token, isAdmin, isRevisor }) {
    console.log('🔍 Props recibidas:', { isAdmin, isRevisor })
    const [proyectos, setProyectos] = useState([]);
    const [revisores, setRevisores] = useState([]);
    const [socios, setSocios] = useState([]);
    const [loading, setLoading] = useState(true);
    // Mensajes por proyecto: { proyectoId: { type: 'error'|'success', text: 'mensaje' } }
    const [mensajesPorProyecto, setMensajesPorProyecto] = useState({});

    // Estado para expandir proyectos
    const [proyectoExpandido, setProyectoExpandido] = useState(null);

    // Estado para asignar revisor
    const [asignandoRevisorA, setAsignandoRevisorA] = useState(null);
    const [revisorSeleccionado, setRevisorSeleccionado] = useState('');

    // Estado para asignar socio
    const [asignandoSocioA, setAsignandoSocioA] = useState(null);
    const [socioSeleccionado, setSocioSeleccionado] = useState('');

    // Estado para cambiar estado
    const [cambiandoEstado, setCambiandoEstado] = useState(null);
    const [nuevoEstado, setNuevoEstado] = useState('');
    const [observaciones, setObservaciones] = useState('');

    // Estados para documentos
    const [documentos, setDocumentos] = useState({});
    const [archivo, setArchivo] = useState(null);
    const [descripcion, setDescripcion] = useState('');
    const [uploadLoading, setUploadLoading] = useState(false);

    // Funciones para manejar mensajes por proyecto
    const setMensajeProyecto = (proyectoId, type, text) => {
        setMensajesPorProyecto(prev => ({
            ...prev,
            [proyectoId]: { type, text }
        }));
        // Auto-limpiar después de 5 segundos
        setTimeout(() => {
            setMensajesPorProyecto(prev => {
                const nuevos = { ...prev };
                delete nuevos[proyectoId];
                return nuevos;
            });
        }, 5000);
    };

    const limpiarMensajeProyecto = (proyectoId) => {
        setMensajesPorProyecto(prev => {
            const nuevos = { ...prev };
            delete nuevos[proyectoId];
            return nuevos;
        });
    };

    // Función optimizada para actualizar solo un proyecto
    const actualizarProyecto = async (proyectoId) => {
        try {
            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const proyectoActualizado = await response.json();
                // Actualizar solo ese proyecto en el estado
                setProyectos(prevProyectos =>
                    prevProyectos.map(p =>
                        p.id === proyectoId ? proyectoActualizado : p
                    )
                );
            }
        } catch (err) {
            console.error('Error actualizando proyecto:', err);
        }
    };

    const cargarProyectos = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/proyectos/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Proyectos cargados:', data);

                let todosProyectos = [];
                if (Array.isArray(data)) {
                    todosProyectos = data;
                } else if (data.results && Array.isArray(data.results)) {
                    todosProyectos = data.results;
                }

                // Para admin: mostrar todos los proyectos
                if (isAdmin) {
                    setProyectos(todosProyectos);
                } else {
                    // Para revisor: solo proyectos pendientes
                    const proyectosPendientes = todosProyectos.filter(p =>
                        ['RECIBIDO', 'ASIGNADO_REVISOR', 'EN_REVISION', 'OBSERVADO'].includes(p.status)
                    );
                    // const proyectosPendientes = todosProyectos;
                    setProyectos(proyectosPendientes);
                }
            } else {
                const errorData = await response.json();
                console.error('Error response:', errorData);
                // Ya no mostramos error global, solo en consola
            }
        } catch (err) {
            console.error('Error en cargarProyectos:', err);
            // Ya no mostramos error global, solo en consola
        } finally {
            setLoading(false);
        }
    }, [token, isAdmin]);

    const cargarRevisores = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/auth/users/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                const usuarios = Array.isArray(data) ? data : (data.results || []);
                const usuariosRevisores = usuarios.filter(u => {
                    const rolPrincipal = (u.rol_principal || '').toUpperCase();
                    return rolPrincipal === 'REVISOR';
                });
                setRevisores(usuariosRevisores);
            }
        } catch (err) {
            console.error('Error cargando revisores:', err);
        }
    }, [token]);

    const cargarSocios = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/auth/users/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                const usuarios = Array.isArray(data) ? data : (data.results || []);
                const usuariosSocios = usuarios.filter(u => {
                    const rolPrincipal = (u.rol_principal || '').toUpperCase();
                    return rolPrincipal === 'SOCIO' && u.is_staff;
                });
                setSocios(usuariosSocios);
            }
        } catch (err) {
            console.error('Error cargando socios:', err);
        }
    }, [token]);

    useEffect(() => {
        cargarProyectos();
        if (isAdmin) {
            cargarRevisores();
            cargarSocios();
        }
    }, [isAdmin, cargarProyectos, cargarRevisores, cargarSocios]);

    const cargarDocumentos = async (proyectoId) => {
        try {
            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/documentos/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setDocumentos(prev => ({
                ...prev,
                [proyectoId]: data.documentos || []
            }));
        } catch (err) {
            console.error('Error al cargar documentos:', err);
        }
    };

    const toggleProyecto = async (proyectoId) => {
        if (proyectoExpandido === proyectoId) {
            setProyectoExpandido(null);
        } else {
            setProyectoExpandido(proyectoId);
            await cargarDocumentos(proyectoId);
        }
    };

    const asignarRevisor = async (proyectoId) => {
        if (!revisorSeleccionado) {
            setMensajeProyecto(proyectoId, 'error', 'Selecciona un revisor');
            return;
        }

        limpiarMensajeProyecto(proyectoId);

        try {
            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/asignar-revisor/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    revisor_id: revisorSeleccionado,
                    staff_role: 'REVISOR'
                })
            });

            const data = await response.json();

            if (response.ok) {
                setMensajeProyecto(proyectoId, 'success', 'Revisor asignado exitosamente');
                setAsignandoRevisorA(null);
                setRevisorSeleccionado('');
                // Actualizar solo este proyecto en lugar de recargar todos
                await actualizarProyecto(proyectoId);
            } else {
                setMensajeProyecto(proyectoId, 'error', data.detail || 'Error al asignar revisor');
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error de conexión: ' + err.message);
        }
    };

    const asignarSocio = async (proyectoId) => {
        if (!socioSeleccionado) {
            setMensajeProyecto(proyectoId, 'error', 'Selecciona un socio');
            return;
        }

        limpiarMensajeProyecto(proyectoId);

        try {
            // Primero verificar cuántos proyectos tiene el socio
            const checkResponse = await apiFetch(
                `${API_URL}/proyectos/${proyectoId}/asignaciones/?check_staff_id=${socioSeleccionado}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const checkData = await checkResponse.json();

            // Si requiere confirmación (tiene 3+ proyectos)
            if (checkData.requiere_confirmacion) {
                const proyectosCount = checkData.proyectos_actuales;

                // Primera confirmación
                const primeraConfirmacion = window.confirm(
                    `⚠️ ADVERTENCIA\n\n` +
                    `El socio "${checkData.username}" ya tiene ${proyectosCount} proyecto(s) asignado(s).\n` +
                    `Se recomienda un máximo de 3 proyectos por socio.\n\n` +
                    `¿Desea continuar y asignarlo al proyecto ${proyectosCount + 1}?`
                );

                if (!primeraConfirmacion) {
                    setMensajeProyecto(proyectoId, 'error', 'Asignación cancelada');
                    return;
                }

                // Segunda confirmación
                const segundaConfirmacion = window.confirm(
                    `⚠️ CONFIRMACIÓN FINAL\n\n` +
                    `¿Está seguro que desea asignar al socio "${checkData.username}" ` +
                    `a este proyecto?\n\n` +
                    `Esto será el proyecto número ${proyectosCount + 1} para este socio.`
                );

                if (!segundaConfirmacion) {
                    setMensajeProyecto(proyectoId, 'error', 'Asignación cancelada');
                    return;
                }
            }

            // Proceder con la asignación
            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/asignaciones/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    staff_id: socioSeleccionado
                })
            });

            const data = await response.json();

            if (response.ok) {
                // Verificar si hay advertencia en la respuesta
                if (data.warning) {
                    setMensajeProyecto(proyectoId, 'success', `Socio asignado exitosamente. ⚠️ ${data.warning.message}`);
                } else {
                    setMensajeProyecto(proyectoId, 'success', 'Socio asignado exitosamente');
                }
                setAsignandoSocioA(null);
                setSocioSeleccionado('');
                // Actualizar solo este proyecto en lugar de recargar todos
                await actualizarProyecto(proyectoId);
            } else {
                setMensajeProyecto(proyectoId, 'error', data.detail || 'Error al asignar socio');
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error de conexión: ' + err.message);
        }
    };

    const eliminarAsignacion = async (proyectoId, staffId) => {
        if (!window.confirm('¿Eliminar esta asignación?')) return;

        try {
            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/asignaciones/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ staff_id: staffId })
            });

            if (response.ok) {
                setMensajeProyecto(proyectoId, 'success', 'Asignación eliminada');
                // Actualizar solo este proyecto en lugar de recargar todos
                await actualizarProyecto(proyectoId);
            } else {
                setMensajeProyecto(proyectoId, 'error', 'Error al eliminar asignación');
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error: ' + err.message);
        }
    };

    const cambiarEstado = async (proyectoId) => {
        if (!nuevoEstado) {
            setMensajeProyecto(proyectoId, 'error', 'Selecciona un estado');
            return;
        }

        // Validar que si es OBSERVADO, se incluyan observaciones
        if (nuevoEstado === 'OBSERVADO' && !observaciones.trim()) {
            setMensajeProyecto(proyectoId, 'error', 'Debes escribir el motivo de la observación');
            return;
        }

        limpiarMensajeProyecto(proyectoId);

        try {
            const bodyData = { status: nuevoEstado };

            // Solo incluir observaciones si el estado es OBSERVADO
            if (nuevoEstado === 'OBSERVADO') {
                bodyData.observaciones = observaciones;
            }

            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(bodyData)
            });

            const data = await response.json();

            if (response.ok) {
                setMensajeProyecto(proyectoId, 'success', `Estado cambiado a ${nuevoEstado}`);
                setCambiandoEstado(null);
                setNuevoEstado('');
                setObservaciones('');
                // Actualizar solo este proyecto
                await actualizarProyecto(proyectoId);
            } else {
                setMensajeProyecto(proyectoId, 'error', data.detail || 'Error al cambiar estado');
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error de conexión: ' + err.message);
        }
    };

    const eliminarProyecto = async (proyectoId) => {
        if (!window.confirm('¿Eliminar proyecto? Esta acción no se puede deshacer.')) return;

        try {
            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                // No mostramos mensaje porque el proyecto ya no existirá en la UI
                // Eliminar del estado local directamente (más rápido que recargar)
                setProyectos(prevProyectos => prevProyectos.filter(p => p.id !== proyectoId));
            } else {
                setMensajeProyecto(proyectoId, 'error', 'Error al eliminar proyecto');
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error: ' + err.message);
        }
    };

    const handleFileChange = (e, proyectoId) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setMensajeProyecto(proyectoId, 'error', 'Archivo muy grande (máx 10MB)');
                setArchivo(null);
                return;
            }
            setArchivo(file);
            limpiarMensajeProyecto(proyectoId);
        }
    };

    const subirDocumento = async (e, proyectoId) => {
        e.preventDefault();
        if (!archivo) return;

        setUploadLoading(true);
        limpiarMensajeProyecto(proyectoId);

        try {
            const formData = new FormData();
            formData.append('archivo', archivo);
            formData.append('descripcion', descripcion);

            const response = await apiFetch(`${API_URL}/proyectos/${proyectoId}/documentos/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                setMensajeProyecto(proyectoId, 'success', 'Documento subido');
                setArchivo(null);
                setDescripcion('');
                document.getElementById(`fileInput_${proyectoId}`).value = '';
                cargarDocumentos(proyectoId);
            } else {
                const data = await response.json();
                setMensajeProyecto(proyectoId, 'error', data.detail || 'Error al subir');
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error: ' + err.message);
        } finally {
            setUploadLoading(false);
        }
    };

    const descargarDocumento = async (docId, proyectoId) => {
        try {
            const response = await apiFetch(`${API_URL}/documentos/${docId}/download/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok) {
                window.open(data.download_link, '_blank');
                setMensajeProyecto(proyectoId, 'success', 'Link generado');
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error al descargar');
        }
    };

    const compartirDocumento = async (docId, proyectoId) => {
        try {
            const response = await apiFetch(`${API_URL}/documentos/${docId}/share/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok) {
                navigator.clipboard.writeText(data.shared_link);
                setMensajeProyecto(proyectoId, 'success', 'Link copiado al portapapeles');
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error al compartir');
        }
    };

    const eliminarDocumento = async (docId, proyectoId) => {
        if (!window.confirm('¿Eliminar documento?')) return;

        try {
            const response = await apiFetch(`${API_URL}/documentos/${docId}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setMensajeProyecto(proyectoId, 'success', 'Documento eliminado');
                cargarDocumentos(proyectoId);
            }
        } catch (err) {
            setMensajeProyecto(proyectoId, 'error', 'Error al eliminar');
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    if (loading) return <p>Cargando proyectos...</p>;

    return (
        <div className="section">
            <h2>{isAdmin ? 'Gestión de Proyectos' : 'Proyectos Asignados'}</h2>

            {proyectos.length === 0 && !loading ? (
                        <div className="proyectos-empty">
                            <p className="proyectos-empty-text">
                                {isAdmin
                                    ? 'No hay proyectos en el sistema.'
                                    : 'No tienes proyectos asignados para revisar. Espera a que un administrador te asigne un proyecto.'
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="proyectos-grid">
                    {proyectos.map((proyecto) => (
                        <div
                            key={proyecto.id}
                            className={`proyecto-card ${proyectoExpandido === proyecto.id ? 'expanded' : ''}`}
                        >
                            {/* Cabecera del proyecto - expandible */}
                            <div onClick={() => toggleProyecto(proyecto.id)} className={`proyecto-header ${proyectoExpandido === proyecto.id ? 'expanded' : ''}`}>
                                <div>
                                    <h3 className="project-title">{proyectoExpandido === proyecto.id ? '▼' : '▶'} {proyecto.nombre_proy}</h3>
                                    <p className="project-sub">{proyecto.propietario} | {proyecto.zona} | <strong>Estado: {proyecto.status}</strong></p>
                                    <p className="project-meta">Creado por: {proyecto.creado_por_username || 'Desconocido'} | Fecha: {new Date(proyecto.created_at).toLocaleDateString('es-BO')}</p>
                                </div>
                                {isAdmin && (
                                    <DashboardButton
                                        onClick={(e) => { e.stopPropagation(); eliminarProyecto(proyecto.id); }}
                                        variant="danger"
                                    >
                                        🗑️ Eliminar Proyecto
                                    </DashboardButton>
                                )}
                            </div>

                            {/* Contenido expandido */}
                            {proyectoExpandido === proyecto.id && (
                                <div className="proyecto-body">
                                    {/* Mensaje de alerta por proyecto */}
                                    {mensajesPorProyecto[proyecto.id] && (
                                        <div className={`mensaje ${mensajesPorProyecto[proyecto.id].type === 'error' ? 'error' : 'success'}`}>
                                            <span>{mensajesPorProyecto[proyecto.id].text}</span>
                                            <button
                                                onClick={() => limpiarMensajeProyecto(proyecto.id)}
                                                className="mensaje-close"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}

                                    {/* Mostrar observaciones si el proyecto está OBSERVADO */}
                                    {proyecto.status === 'OBSERVADO' && proyecto.observaciones && (
                                        <div className="observado-box">
                                            <h4 className="observado-title">⚠️ Proyecto Observado</h4>
                                            <div className="observado-inner">
                                                <strong className="observado-strong">Motivo de la observación:</strong>
                                                <p className="observado-text">{proyecto.observaciones}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Mostrar revisores y socios asignados */}
                                    {proyecto.asignaciones && proyecto.asignaciones.length > 0 && (
                                        <div className="asignaciones-box">
                                            <h4 className="asignaciones-title">👥 Usuarios Asignados ({proyecto.asignaciones.length})</h4>
                                            <table className="asignaciones-table">
                                                <thead>
                                                    <tr className="asignaciones-thead">
                                                        <th className="th">Nombre</th>
                                                        <th className="th">Rol</th>
                                                        <th className="th">Fecha Asignación</th>
                                                        {isAdmin && <th className="th">Acciones</th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        // Contar revisores para asignar letras solo a ellos
                                                        let contadorRevisor = 0;
                                                        return proyecto.asignaciones.map((asig, idx) => {
                                                            // Solo incrementar si es revisor
                                                            const letraRevisor = asig.staff_role === 'REVISOR'
                                                                ? String.fromCharCode(65 + contadorRevisor++)
                                                                : '';

                                                            return (
                                                                <tr key={idx}>
                                                                    <td className="td">
                                                                        <span className={`badge ${asig.staff_role === 'REVISOR' ? 'revisor' : 'socio'}`}>
                                                                            {asig.staff_role === 'REVISOR' ? `Revisor ${letraRevisor}` : 'Socio'}
                                                                        </span>
                                                                        {asig.staff_nombre || asig.staff_email}
                                                                    </td>
                                                                <td className="td">
                                                                    <span className={`role-badge ${asig.staff_role === 'REVISOR' ? 'revisor' : 'socio'}`}>
                                                                        {asig.staff_role}
                                                                    </span>
                                                                </td>
                                                                <td className="td">
                                                                    {new Date(asig.fecha_asignacion).toLocaleDateString('es-BO')}
                                                                </td>
                                                                {isAdmin && (
                                                                    <td className="td td-center">
                                                                        <DashboardButton
                                                                            onClick={() => eliminarAsignacion(proyecto.id, asig.staff_id)}
                                                                            variant="danger"
                                                                            size="sm"
                                                                        >
                                                                            ❌ Eliminar
                                                                        </DashboardButton>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        );
                                                        });
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* Asignar Revisor (solo admin) */}
                                    {isAdmin && (
                                        <div className="panel-neutral">
                                            <h4 className="panel-title">👤 {proyecto.asignaciones && proyecto.asignaciones.some(a => a.staff_role === 'REVISOR') ? 'Asignar Otro Revisor' : 'Asignar Revisor'}</h4>
                                            {asignandoRevisorA === proyecto.id ? (
                                                <div>
                                                    <select
                                                        value={revisorSeleccionado}
                                                        onChange={(e) => setRevisorSeleccionado(e.target.value)}
                                                        className="form-select"
                                                    >
                                                        <option value="">-- Seleccionar Revisor --</option>
                                                        {revisores.map(rev => (
                                                            <option key={rev.id} value={rev.id}>
                                                                {rev.username} ({rev.email})
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <small className="small-note">Puedes asignar N revisores a cada proyecto</small>
                                                    <div className="btn-row">
                                                        <DashboardButton onClick={() => asignarRevisor(proyecto.id)} variant="warning">Asignar</DashboardButton>
                                                        <DashboardButton onClick={() => { setAsignandoRevisorA(null); setRevisorSeleccionado(''); }} variant="secondary">Cancelar</DashboardButton>
                                                    </div>
                                                </div>
                                            ) : (
                                                <DashboardButton onClick={() => setAsignandoRevisorA(proyecto.id)} variant="warning">👤 Asignar Revisor</DashboardButton>
                                            )}
                                        </div>
                                    )}

                                    {/* Asignar Socio (solo admin) */}
                                    {isAdmin && (
                                        <div className="panel-warning">
                                            <h4 className="panel-title">👥 Asignar Socio (Staff) al Proyecto</h4>
                                            {asignandoSocioA === proyecto.id ? (
                                                <div>
                                                    <select value={socioSeleccionado} onChange={(e) => setSocioSeleccionado(e.target.value)} className="form-select">
                                                        <option value="">-- Seleccionar Socio (Staff) --</option>
                                                        {socios.map(socio => (
                                                            <option key={socio.id} value={socio.id}>
                                                                {socio.username} ({socio.email})
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <small className="small-note">Solo se muestran socios que son miembros del staff (máximo 3 por proyecto)</small>
                                                    <div className="btn-row">
                                                        <DashboardButton onClick={() => asignarSocio(proyecto.id)} variant="warning">Asignar Socio</DashboardButton>
                                                        <DashboardButton onClick={() => { setAsignandoSocioA(null); setSocioSeleccionado(''); }} variant="secondary">Cancelar</DashboardButton>
                                                    </div>
                                                </div>
                                            ) : (
                                                <DashboardButton onClick={() => setAsignandoSocioA(proyecto.id)} variant="warning">👥 Asignar Socio (Staff)</DashboardButton>
                                            )}
                                        </div>
                                    )}

                                    {/* Cambiar Estado */}
                                    <div className="panel-info">
                                        <h4 className="panel-title">🔄 Cambiar Estado del Proyecto</h4>
                                        {cambiandoEstado === proyecto.id ? (
                                            <div>
                                                <select value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value)} className="form-select">
                                                    <option value="">-- Seleccionar Estado --</option>
                                                    <option value="RECIBIDO">RECIBIDO</option>
                                                    <option value="ASIGNADO_REVISOR">ASIGNADO_REVISOR</option>
                                                    <option value="EN_REVISION">EN_REVISION</option>
                                                    <option value="OBSERVADO">OBSERVADO</option>
                                                    <option value="APROBADO">APROBADO</option>
                                                    <option value="RECHAZADO">RECHAZADO</option>
                                                </select>

                                                {/* Mostrar campo de observaciones si se selecciona OBSERVADO */}
                                                {nuevoEstado === 'OBSERVADO' && (
                                                    <div className="mb-10">
                                                        <label className="label-strong">Motivo de la Observación: *</label>
                                                        <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Escribe el motivo por el cual el proyecto está observado..." className="form-textarea" required />
                                                    </div>
                                                )}

                                                <div className="btn-row">
                                                    <DashboardButton onClick={() => cambiarEstado(proyecto.id)} variant="warning">Cambiar</DashboardButton>
                                                    <DashboardButton onClick={() => { setCambiandoEstado(null); setNuevoEstado(''); setObservaciones(''); }} variant="secondary">Cancelar</DashboardButton>
                                                </div>
                                            </div>
                                        ) : (
                                            <DashboardButton onClick={() => setCambiandoEstado(proyecto.id)} variant="warning">🔄 Cambiar Estado</DashboardButton>
                                        )}
                                    </div>

                                    {/* Documentos del Proyecto */}
                                    {(isAdmin || isRevisor) && (
                                        <div className="panel-docs">
                                            <h4 className="panel-title">📄 Documentos del Proyecto ({documentos[proyecto.id]?.length || 0})</h4>

                                            {/* Formulario para subir documentos */}
                                            <form onSubmit={(e) => subirDocumento(e, proyecto.id)} className="form-card">
                                                <h5 className="doc-title">Subir Nuevo Documento</h5>
                                                <div className="mb-10">
                                                    <input
                                                        id={`fileInput_${proyecto.id}`}
                                                        type="file"
                                                        onChange={(e) => handleFileChange(e, proyecto.id)}
                                                        disabled={uploadLoading}
                                                        className="file-input"
                                                    />
                                                    {archivo && (
                                                        <span className="file-info">
                                                            ✓ {archivo.name} ({formatBytes(archivo.size)})
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mb-10">
                                                    <textarea
                                                        value={descripcion}
                                                        onChange={(e) => setDescripcion(e.target.value)}
                                                        placeholder="Descripción (opcional)"
                                                        disabled={uploadLoading}
                                                        className="form-textarea"
                                                        rows="2"
                                                    />
                                                </div>
                                                <DashboardButton type="submit" disabled={uploadLoading || !archivo} variant="success">
                                                    {uploadLoading ? 'Subiendo...' : '⬆️ Subir Documento'}
                                                </DashboardButton>
                                            </form>

                                            {/* Lista de documentos */}
                                            {!documentos[proyecto.id] || documentos[proyecto.id].length === 0 ? (
                                                <p className="no-docs">No hay documentos en este proyecto</p>
                                            ) : (
                                                <div>
                                                    {documentos[proyecto.id].map((doc) => (
                                                        <div key={doc.id} className="document-item">
                                                            <div className="doc-row">
                                                                <div className="doc-left">
                                                                                        <h5 className="doc-title">📄 {doc.nombre_archivo}</h5>
                                                                                        {doc.descripcion && (
                                                                                            <p className="doc-desc">{doc.descripcion}</p>
                                                                                        )}
                                                                                        <p className="doc-meta">
                                                                        {formatBytes(doc.tamaño_bytes)} |{' '}
                                                                        {new Date(doc.fecha_subida).toLocaleString('es-BO')} |{' '}
                                                                        Subido por: {doc.subido_por_detalle?.nombre || 'N/A'}
                                                                    </p>
                                                                </div>
                                                                                    <div className="document-actions">
                                                                    <DashboardButton onClick={() => descargarDocumento(doc.id, proyecto.id)} variant="view" size="sm">⬇️ Descargar</DashboardButton>
                                                                    <DashboardButton onClick={() => compartirDocumento(doc.id, proyecto.id)} variant="warning" size="sm">🔗 Compartir</DashboardButton>
                                                                    <DashboardButton onClick={() => eliminarDocumento(doc.id, proyecto.id)} variant="danger" size="sm">🗑️ Eliminar</DashboardButton>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default ProyectosPendientes;
